import { CopyObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { GetParametersCommand, SSMClient } from '@aws-sdk/client-ssm';
import { fetchAuthSession } from 'aws-amplify/auth';
import * as Sentry from '@sentry/react';

export async function copyDocFromInternalToCustomer({ internalDocPath, customerDocPath, customerDocName }) {
  const { credentials } = await fetchAuthSession();
  const s3Client = new S3Client({
    region: 'us-east-1',
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken
    }
  });
  const ssmClient = new SSMClient({
    region: 'us-east-1',
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken
    }
  });

  // v3's send() rejects where the v2 callback resolved undefined on error. Keep falling through to
  // the copy (so the outcome is still { success: false }, as before), but report the cause: without
  // this, an SSM failure — a missing GetParameters permission, say — is invisible, and only the
  // downstream copy failure caused by an undefined bucket gets captured, which reads as an S3 fault.
  const getParametersResponse = await ssmClient
    .send(
      new GetParametersCommand({
        Names: ['POLICY_DOCS_S3_BUCKET'],
        WithDecryption: true
      })
    )
    .catch((ssmError) => {
      Sentry.captureException(ssmError, { tags: { flow: 'copyDocFromInternalToCustomer.getBucketParam' } });
      return undefined;
    });

  // SSM returns 200 with Parameters: [] (plus InvalidParameters) when the name doesn't exist, so
  // index 0 has to be optional too — otherwise this throws a TypeError outside the try below and
  // the caller's .then() has no .catch(), leaving the modal spinning with no toast.
  const bucket = getParametersResponse?.Parameters?.[0]?.Value;
  try {
    const data = await s3Client.send(
      new CopyObjectCommand({
        Bucket: bucket,
        CopySource: `${bucket}/${internalDocPath}`,
        Key: `${customerDocPath}/${customerDocName}`
      })
    );
    return { data, success: true };
  } catch (err) {
    // The caller reports the { success: false } outcome to Sentry but not the underlying error,
    // so capture it here to keep the S3/SSM cause instead of only the failed-copy symptom.
    Sentry.captureException(err, { tags: { flow: 'copyDocFromInternalToCustomer' }, extra: { bucket } });
    return { success: false };
  }
}
